import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { deleteRegistration, getAdminImgBBKey, loadRegistrations, setRegistrationDone } from './HomePage';
import { createPublicContent, loadPublicContent, removePublicContent, uploadImageToImgBB } from '../utils/siteContent';
import './AdminDashboard.css';

const dateKey = (value) => {
  if (!value) return '';
  const date = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const dateValue = (value) => {
  if (!value) return new Date(0);
  const date = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? new Date(0) : date;
};

const todayKey = () => dateKey(new Date());

const registrationDate = (row) => row.createdAt || row.date || row.timestamp;

export function AdminDashboard() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [selectedDate, setSelectedDate] = useState(todayKey);
  const [expandedId, setExpandedId] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [copied, setCopied] = useState('');
  const [confirmRegistration, setConfirmRegistration] = useState(null);
  const [isUpdatingRegistration, setIsUpdatingRegistration] = useState(false);
  const [adminTab, setAdminTab] = useState('registrations');
  const [content, setContent] = useState({ proofs: [], reviews: [], videos: [], salaries: [] });
  const [contentError, setContentError] = useState('');
  const [isSavingContent, setIsSavingContent] = useState(false);

  useEffect(() => {
    const session = localStorage.getItem('earneeds_admin_session');
    if (!session) {
      navigate('/admin-login', { replace: true });
      return;
    }

    Promise.all([
      loadRegistrations(),
      loadPublicContent('proofs'),
      loadPublicContent('reviews'),
      loadPublicContent('videos'),
      loadPublicContent('salaries')
    ]).then(([registrations, proofs, reviews, videos, salaries]) => {
      setRows(registrations);
      setContent((current) => {
        const merge = (loaded, added) => [
          ...added,
          ...loaded.filter((item) => !added.some((entry) => entry.id === item.id))
        ];
        return {
          proofs: merge(proofs, current.proofs),
          reviews: merge(reviews, current.reviews),
          videos: merge(videos, current.videos),
          salaries: merge(salaries, current.salaries)
        };
      });
    });
  }, [navigate]);

  useEffect(() => {
    const now = new Date();
    const resetAt = new Date(now);
    resetAt.setHours(0, 59, 0, 0);
    if (resetAt <= now) resetAt.setDate(resetAt.getDate() + 1);

    const resetTimer = window.setTimeout(async () => {
      setSelectedDate(todayKey());
      setExpandedId('');
      setRows(await loadRegistrations());
    }, resetAt.getTime() - now.getTime());

    return () => window.clearTimeout(resetTimer);
  }, []);

  const visibleRows = useMemo(() => rows
    .filter((row) => dateKey(registrationDate(row)) === selectedDate)
    .sort((left, right) => dateValue(registrationDate(left)) - dateValue(registrationDate(right))), [rows, selectedDate]);

  const handleLogout = () => {
    localStorage.removeItem('earneeds_admin_session');
    navigate('/', { replace: true });
  };

  const handleCopy = async (value, type) => {
    try {
      await navigator.clipboard.writeText(value || '');
      setCopied(type);
      setTimeout(() => setCopied(''), 1200);
    } catch (error) {
      setCopied('');
    }
  };

  const openRegistrationActions = (event, row) => {
    event.stopPropagation();
    setConfirmRegistration(row);
  };

  const handleConfirmDone = async () => {
    if (!confirmRegistration || confirmRegistration.done) return;
    setIsUpdatingRegistration(true);
    const saved = await setRegistrationDone(confirmRegistration, true);
    setIsUpdatingRegistration(false);
    if (saved) {
      setRows((current) => current.map((item) => item.id === confirmRegistration.id ? { ...item, done: true } : item));
      setConfirmRegistration(null);
    }
  };

  const handleDeleteRegistration = async () => {
    if (!confirmRegistration) return;
    setIsUpdatingRegistration(true);
    const deleted = await deleteRegistration(confirmRegistration);
    setIsUpdatingRegistration(false);
    if (deleted) {
      setRows((current) => current.filter((item) => item.id !== confirmRegistration.id));
      setExpandedId('');
      setConfirmRegistration(null);
    }
  };

  const rowId = (row) => row.id || `${row.name}-${row.phone}-${registrationDate(row)}`;

  const saveContentWithImage = async (event, type) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const image = data.get('image');
    setIsSavingContent(true);
    setContentError('');

    try {
      const apiKey = await getAdminImgBBKey();
      const imageUrl = await uploadImageToImgBB(image, apiKey);
      const payload = type === 'proofs'
        ? { caption: String(data.get('caption') || '').trim(), imageUrl }
        : {
          name: String(data.get('name') || '').trim(),
          text: String(data.get('text') || '').trim(),
          profileUrl: imageUrl
        };
      const saved = await createPublicContent(type, payload);
      setContent((current) => ({ ...current, [type]: [saved, ...current[type]] }));
      form.reset();
    } catch (error) {
      setContentError(error.message || 'কনটেন্ট save করা যায়নি।');
    } finally {
      setIsSavingContent(false);
    }
  };

  const saveVideo = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const payload = {
      title: String(data.get('title') || '').trim(),
      url: String(data.get('url') || '').trim()
    };
    if (!payload.title || !/^https?:\/\//i.test(payload.url)) {
      setContentError('ভিডিওর নাম এবং সঠিক https:// লিংক দিন।');
      return;
    }

    setIsSavingContent(true);
    setContentError('');
    try {
      const saved = await createPublicContent('videos', payload);
      setContent((current) => ({ ...current, videos: [saved, ...current.videos] }));
      form.reset();
    } catch (error) {
      setContentError(error.message || 'ভিডিও লিংক save করা যায়নি।');
    } finally {
      setIsSavingContent(false);
    }
  };

  const saveSalary = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const payload = {
      task: String(data.get('task') || '').trim(),
      unit: String(data.get('unit') || '').trim(),
      amount: String(data.get('amount') || '').trim()
    };

    if (!payload.task || !payload.unit || !payload.amount) {
      setContentError('কাজ, হিসাবের একক এবং পারিশ্রমিক পূরণ করুন।');
      return;
    }

    setIsSavingContent(true);
    setContentError('');
    try {
      const saved = await createPublicContent('salaries', payload);
      setContent((current) => ({ ...current, salaries: [saved, ...current.salaries] }));
      form.reset();
    } catch (error) {
      setContentError(error.message || 'সেলারি তথ্য save করা যায়নি।');
    } finally {
      setIsSavingContent(false);
    }
  };

  const removeContent = async (type, item) => {
    try {
      await removePublicContent(type, item.id);
      setContent((current) => ({ ...current, [type]: current[type].filter((entry) => entry.id !== item.id) }));
    } catch (error) {
      setContentError('কনটেন্ট delete করা যায়নি।');
    }
  };

  return (
    <div className="admin-layout">
      <header className="admin-topbar">
        <div className="admin-brand">
          <img src="/logo.png" alt="Earneeds logo" />
          <div className="admin-brand-copy">
            <span>Earneeds</span>
            <small>Learn • Work • Earn</small>
          </div>
        </div>
        <nav className="admin-main-nav" aria-label="Main navigation">
          <button type="button" className="active" onClick={() => navigate('/')}>Home</button>
          <button type="button">How It Works</button>
          <button type="button">Earning Options</button>
          <button type="button">Training</button>
          <button type="button">About</button>
        </nav>
        <div className="admin-menu-wrap">
          <button
            className="admin-menu-toggle"
            type="button"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
            aria-controls="admin-menu"
            onClick={() => setMobileMenuOpen((open) => !open)}
          >
            {mobileMenuOpen ? <X size={25} /> : <Menu size={25} />}
          </button>
          {mobileMenuOpen && (
            <div className="admin-menu-panel" id="admin-menu">
              <button type="button" onClick={handleLogout}>Logout</button>
            </div>
          )}
        </div>
      </header>

      <main className="admin-main">
        <nav className="admin-content-tabs" aria-label="Dashboard sections">
          {[
            ['registrations', 'Registrations'],
            ['proofs', 'পেমেন্ট প্রুফ'],
            ['reviews', 'ইউজার রিভিউ'],
            ['videos', 'কাজের ভিডিও'],
            ['salaries', 'সেলারি লিস্ট']
          ].map(([tab, label]) => (
            <button type="button" key={tab} className={adminTab === tab ? 'active' : ''} onClick={() => {
              setAdminTab(tab);
              setContentError('');
            }}>{label}</button>
          ))}
        </nav>

        {adminTab === 'registrations' && (
          <>
            <div className="admin-page-heading">
              <div>
                <small>Dashboard</small>
                <h1>Registration List</h1>
              </div>
              <label className="date-filter">
                <span>Date</span>
                <input type="date" value={selectedDate} onChange={(event) => {
                  setSelectedDate(event.target.value);
                  setExpandedId('');
                }} />
              </label>
            </div>

            <section className="registration-list" aria-label="Registrations">
              <div className="registration-list-heading">
                <strong>{visibleRows.length}</strong>
                <span>registrations on {selectedDate}</span>
              </div>

              {visibleRows.length === 0 ? (
                <div className="empty-state">এই তারিখে কোনো registration নেই।</div>
              ) : visibleRows.map((row, index) => {
                const id = rowId(row);
                const expanded = expandedId === id;
                return (
                  <article className={`registration-card${expanded ? ' is-expanded' : ''}`} key={id}>
                    <div
                      className="registration-summary"
                      role="button"
                      tabIndex={0}
                      aria-expanded={expanded}
                      onClick={() => setExpandedId(expanded ? '' : id)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setExpandedId(expanded ? '' : id);
                        }
                      }}
                    >
                      <span className="registration-number">{index + 1}</span>
                      <span className="registration-person">
                        <strong>{row.name}</strong>
                        <span>{row.phone}</span>
                      </span>
                      <button
                        type="button"
                        className={`done-button${row.done ? ' is-done' : ''}`}
                        onClick={(event) => openRegistrationActions(event, row)}
                      >
                        Done
                      </button>
                    </div>
                    {expanded && (
                      <div className="registration-drawer">
                        <button type="button" onClick={() => handleCopy(row.name, `name-${id}`)}>
                          {copied === `name-${id}` ? 'Copied name' : 'Copy name'}
                        </button>
                        <button type="button" onClick={() => handleCopy(row.phone, `phone-${id}`)}>
                          {copied === `phone-${id}` ? 'Copied number' : 'Copy number'}
                        </button>
                      </div>
                    )}
                  </article>
                );
              })}
            </section>
          </>
        )}

        {adminTab !== 'registrations' && (
          <section className="content-manager">
            <div className="admin-page-heading">
              <div>
                <small>Public website content</small>
                <h1>{adminTab === 'proofs' ? 'পেমেন্ট প্রুফ' : adminTab === 'reviews' ? 'ইউজার রিভিউ' : adminTab === 'videos' ? 'কাজের ভিডিও' : 'সেলারি লিস্ট'}</h1>
              </div>
            </div>

            {contentError && <p className="content-manager-message" role="status">{contentError}</p>}

            {adminTab === 'proofs' && (
              <form className="content-entry-form" onSubmit={(event) => saveContentWithImage(event, 'proofs')}>
                <label>ছবির caption (ঐচ্ছিক)<input name="caption" placeholder="Payment proof caption" /></label>
                <label>Payment proof image<input name="image" type="file" accept="image/*" required /></label>
                <button type="submit" disabled={isSavingContent}>{isSavingContent ? 'Uploading...' : 'ছবি যোগ করুন'}</button>
              </form>
            )}

            {adminTab === 'reviews' && (
              <form className="content-entry-form" onSubmit={(event) => saveContentWithImage(event, 'reviews')}>
                <label>নাম<input name="name" required placeholder="ইউজারের নাম" /></label>
                <label>Profile picture<input name="image" type="file" accept="image/*" required /></label>
                <label>Review<textarea name="text" rows="4" required placeholder="রিভিউ লিখুন" /></label>
                <button type="submit" disabled={isSavingContent}>{isSavingContent ? 'Uploading...' : 'রিভিউ যোগ করুন'}</button>
              </form>
            )}

            {adminTab === 'videos' && (
              <form className="content-entry-form" onSubmit={saveVideo}>
                <label>ভিডিওর নাম<input name="title" required placeholder="কাজের ভিডিওর নাম" /></label>
                <label>Online video link<input name="url" type="url" required placeholder="https://youtube.com/... অথবা video URL" /></label>
                <button type="submit" disabled={isSavingContent}>{isSavingContent ? 'Saving...' : 'ভিডিও যোগ করুন'}</button>
              </form>
            )}

            {adminTab === 'salaries' && (
              <form className="content-entry-form" onSubmit={saveSalary}>
                <label>কাজের নাম<input name="task" required placeholder="যেমন: ফর্ম পূরণ" /></label>
                <label>হিসাবের একক<input name="unit" required placeholder="যেমন: প্রতি ফর্ম" /></label>
                <label>পারিশ্রমিক বা কমিশন<input name="amount" required placeholder="যেমন: ৳ 150, Unlimited বা ২০% কমিশন" /></label>
                <button type="submit" disabled={isSavingContent}>{isSavingContent ? 'Saving...' : 'সেলারি যোগ করুন'}</button>
              </form>
            )}

            <div className="managed-content-list">
              {content[adminTab].map((item) => (
                <article className="managed-content-item" key={item.id}>
                  {item.imageUrl && <img src={item.imageUrl} alt="" />}
                  {item.profileUrl && <img className="managed-avatar" src={item.profileUrl} alt="" />}
                  <div>
                    <strong>{item.caption || item.name || item.title || item.task}</strong>
                    {item.task && <p>{item.unit} · {item.amount}</p>}
                    {item.text && <p>{item.text}</p>}
                    {item.url && <small>{item.url}</small>}
                  </div>
                  <button type="button" className="managed-delete-button" onClick={() => removeContent(adminTab, item)}>Delete</button>
                </article>
              ))}
            </div>
          </section>
        )}
      </main>

      {confirmRegistration && (
        <div className="registration-confirm-overlay" onClick={() => setConfirmRegistration(null)}>
          <section
            className="registration-confirm-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="registration-confirm-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="registration-confirm-title">Registration action</h2>
            <p>{confirmRegistration.name} · {confirmRegistration.phone}</p>
            <div className="registration-confirm-actions">
              {!confirmRegistration.done && (
                <button type="button" className="confirm-done-button" onClick={handleConfirmDone} disabled={isUpdatingRegistration}>
                  OK
                </button>
              )}
              <button type="button" className="confirm-delete-button" onClick={handleDeleteRegistration} disabled={isUpdatingRegistration}>
                {isUpdatingRegistration ? 'Please wait...' : 'Delete'}
              </button>
              <button type="button" className="confirm-cancel-button" onClick={() => setConfirmRegistration(null)} disabled={isUpdatingRegistration}>
                Cancel
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}